import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Loader2, Upload, ArrowUp, ArrowDown } from 'lucide-react';
import { Video } from '@/types/hierarchicalFolders';
import { useHierarchicalFoldersStore } from '@/store/hierarchicalFoldersStore';
import { hierarchicalFoldersService } from '@/services/hierarchicalFoldersService';
import { uploadService } from '@/services/uploadService';
import { toast } from 'sonner';
import { nanoid } from 'nanoid';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';

interface VideoSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folderId: string;
  onOpenUploadQueue?: () => void;
}

interface UploadFileItem {
  file: File;
  id: string;
  thumbnail: string;
  status: 'idle' | 'generating-thumb' | 'uploading' | 'completed';
  progress?: number;
}

// Format duration from seconds to mm:ss
const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Generate video thumbnail
const generateVideoThumbnail = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.src = url;
    video.currentTime = 1;
    
    video.addEventListener('loadeddata', () => {
      const canvas = document.createElement('canvas');
      canvas.width = 96;
      canvas.height = 54;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, 96, 54);
        const thumbnail = canvas.toDataURL('image/jpeg', 0.7);
        URL.revokeObjectURL(url);
        resolve(thumbnail);
      } else {
        URL.revokeObjectURL(url);
        resolve('/placeholder.svg');
      }
    });
    
    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      resolve('/placeholder.svg');
    });
  });
};

export function VideoSelectorModal({
  open,
  onOpenChange,
  folderId,
  onOpenUploadQueue
}: VideoSelectorModalProps) {
  useLanguage();
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFileItem[]>([]);
  const [fileToRemove, setFileToRemove] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [sortField, setSortField] = useState<'createdAt' | 'plays' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isUploading, setIsUploading] = useState(false);
  
  const addVideosToFolder = useHierarchicalFoldersStore(state => state.addVideosToFolder);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('videos', 'edit');

  useEffect(() => {
    if (open && !canEdit) {
      toast.error(translateInterfaceText("Sem permissão para adicionar vídeos"));
      onOpenChange(false);
    }
  }, [open, canEdit, onOpenChange]);
  
  // Load available videos
  useEffect(() => {
    if (open) {
      loadVideos();
    }
  }, [open, searchQuery]);
  
  const loadVideos = async () => {
    setIsLoading(true);
    try {
      const result = await hierarchicalFoldersService.listAvailableVideos({
        q: searchQuery
      });
      setVideos(result.items);
    } catch (error) {
      toast.error(translateInterfaceText("Erro ao carregar vídeos"));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSort = (field: 'createdAt' | 'plays') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  const getSortedVideos = (videosToSort: Video[]) => {
    if (!sortField) return videosToSort;
    
    return [...videosToSort].sort((a, b) => {
      let aValue: number;
      let bValue: number;
      
      if (sortField === 'createdAt') {
        aValue = new Date(a.createdAt).getTime();
        bValue = new Date(b.createdAt).getTime();
      } else {
        aValue = a.plays || 0;
        bValue = b.plays || 0;
      }
      
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
  };
  
  const handleToggleVideo = (videoId: string) => {
    setSelectedIds(prev =>
      prev.includes(videoId)
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };
  
  const handleSubmit = async () => {
    if (selectedIds.length === 0) return;

    setIsSaving(true);
    try {
      await addVideosToFolder(folderId, selectedIds);
      
      toast.success(translateInterfaceText("Vídeos adicionados"), {
        description: translateInterfaceText("{value0} vídeo(s) adicionado(s) à pasta", { value0: (selectedIds.length) })
      });

      setSelectedIds([]);
      onOpenChange(false);
    } catch (error) {
      toast.error(translateInterfaceText("Erro ao adicionar vídeos"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileSelection = async (files: FileList | null) => {
    if (!files) return;
    
    const fileArray = Array.from(files);
    
    // Add files immediately with generating-thumb status
    const newItems: UploadFileItem[] = fileArray.map(file => ({
      file,
      id: nanoid(),
      thumbnail: '/placeholder.svg',
      status: 'generating-thumb' as const,
      progress: 0
    }));
    
    setUploadFiles(prev => [...prev, ...newItems]);
    
    // Generate thumbnails in parallel
    for (const item of newItems) {
      const thumb = await generateVideoThumbnail(item.file);
      setUploadFiles(prev => prev.map(f => 
        f.id === item.id 
          ? { ...f, thumbnail: thumb, status: 'uploading' as const }
          : f
      ));
    }
    
    // Automatically start upload after thumbnails are generated
    setIsUploading(true);
    
    // Simulate upload progress
    const uploadPromises = newItems.map(async (item) => {
      // Simulate progress from 0 to 100
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 200));
        setUploadFiles(prev => prev.map(f => 
          f.id === item.id 
            ? { ...f, progress: i, status: i === 100 ? 'completed' as const : 'uploading' as const }
            : f
        ));
      }
    });
    
    await Promise.all(uploadPromises);
    
    // Add to uploadService queue
    const extractedFiles = fileArray;
    uploadService.enqueue(extractedFiles, folderId);
    
    toast.success(translateInterfaceText("Vídeos adicionados à fila"), {
      description: translateInterfaceText("{value0} vídeo(s) em processamento", { value0: (extractedFiles.length) })
    });
    
    setIsUploading(false);
  };

  const handleCloseAfterUpload = () => {
    setUploadFiles([]);
    setIsUploading(false);
    onOpenChange(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelection(files);
    }
  };
  
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[740px] h-[620px] max-w-[92vw] max-h-[88vh] p-0 rounded-xl overflow-hidden border border-[rgba(0,0,0,0.06)] shadow-[0_8px_20px_rgba(0,0,0,0.15)] bg-white flex flex-col" hideCloseButton>
        <DialogHeader className="px-8 pt-8 pb-4 flex-shrink-0">
          <DialogTitle className="text-[18px] font-semibold text-[#1A1A1A] flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#3B82F6]" />
            {translateInterfaceText("Importar vídeos")}</DialogTitle>
          <p className="text-[14px] font-normal text-[rgba(0,0,0,0.55)] mt-2">
            {translateInterfaceText("Selecione ou arraste os arquivos de vídeo para importar na sua biblioteca.")}</p>
        </DialogHeader>
        
        <Tabs defaultValue="library" className="w-full px-8 pb-4 flex flex-col flex-1 min-h-0">
          <TabsList className="grid grid-cols-2 gap-1 flex-shrink-0 mb-0 bg-transparent h-auto p-0">
            <TabsTrigger 
              value="library"
              className="flex-1 h-[44px] px-0 py-3 rounded-t-md rounded-b-none font-semibold text-[14px] data-[state=active]:bg-[#2D6BFF] data-[state=active]:text-white data-[state=active]:border-0 data-[state=inactive]:bg-white data-[state=inactive]:text-[#64748B] data-[state=inactive]:border data-[state=inactive]:border-[#D8DDE7] data-[state=inactive]:hover:bg-[#F5F7FA] data-[state=inactive]:hover:border-[#C8CFDA] transition-all"
            >
              {translateInterfaceText("Biblioteca")}</TabsTrigger>
            <TabsTrigger 
              value="upload"
              className="flex-1 h-[44px] px-0 py-3 rounded-t-md rounded-b-none font-semibold text-[14px] data-[state=active]:bg-[#2D6BFF] data-[state=active]:text-white data-[state=active]:border-0 data-[state=inactive]:bg-white data-[state=inactive]:text-[#64748B] data-[state=inactive]:border data-[state=inactive]:border-[#D8DDE7] data-[state=inactive]:hover:bg-[#F5F7FA] data-[state=inactive]:hover:border-[#C8CFDA] transition-all"
            >
              {translateInterfaceText("Upload")}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="library" className="flex flex-col h-full mt-0 gap-3 pt-3">
            {/* Search */}
            <div className="relative flex-shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A0A0A0]" />
              <Input
                placeholder={translateInterfaceText("Buscar vídeos...")}
                className="pl-9 h-[42px] border-[rgba(0,0,0,0.08)]"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            {/* Video list - Fixed height with scroll - mostra 4 vídeos */}
            <div className="overflow-hidden h-[350px] flex-shrink-0">
              <div className="h-full overflow-y-auto [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-track]:bg-[#E8ECF2] [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#2D6BFF] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-[#2563EB]">
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : videos.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">
                  {translateInterfaceText("Nenhum vídeo encontrado")}</div>
              ) : (
                <div>
                  {/* Header da tabela */}
                  <div className="grid grid-cols-[auto_96px_1fr_120px_100px] gap-4 px-3 py-2 bg-muted/30 border-b font-medium text-sm text-muted-foreground sticky top-0">
                    <div></div>
                    <div></div>
                    <div>{translateInterfaceText("Nome")}</div>
                    
                    {/* Coluna Criado em - clicável com ordenação */}
                    <button
                      onClick={() => handleSort('createdAt')}
                      className="text-center flex items-center justify-center gap-1 hover:text-foreground transition-colors"
                    >
                      <span>{translateInterfaceText("Criado em")}</span>
                      {sortField === 'createdAt' ? (
                        sortDirection === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowDown className="h-3 w-3 opacity-30" />
                      )}
                    </button>
                    
                    {/* Coluna Plays - clicável com ordenação */}
                    <button
                      onClick={() => handleSort('plays')}
                      className="text-center flex items-center justify-center gap-1 hover:text-foreground transition-colors"
                    >
                      <span>{translateInterfaceText("Plays")}</span>
                      {sortField === 'plays' ? (
                        sortDirection === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowDown className="h-3 w-3 opacity-30" />
                      )}
                    </button>
                  </div>
                  
                  {/* Linhas da tabela */}
                  <div className="divide-y">
                    {getSortedVideos(videos).map(video => (
                      <div
                        key={video.id}
                        className="grid grid-cols-[auto_96px_1fr_120px_100px] gap-4 items-center p-2.5 transition-colors cursor-pointer"
                        onClick={() => handleToggleVideo(video.id)}
                      >
                        <Checkbox
                          checked={selectedIds.includes(video.id)}
                          onCheckedChange={() => handleToggleVideo(video.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        
                        <div className="cursor-pointer">
                          <img
                            src={video.thumbnailUrl || '/placeholder.svg'}
                            alt={video.title}
                            className="w-[96px] h-[54px] object-cover rounded-[6px] border border-[#E2E8F0] bg-[#F1F5F9]"
                          />
                        </div>
                        
                        <div className="min-w-0">
                          <h4 className="font-medium text-sm truncate text-muted-foreground">{video.title}</h4>
                        </div>
                        
                        <div className="text-center text-sm text-muted-foreground">
                          {new Date(video.createdAt).toLocaleDateString(getInterfaceLanguage(), {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit'
                          })}
                        </div>
                        
                        <div className="text-center text-sm text-muted-foreground">
                          {video.plays || 0}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </div>
            
            <div className="flex-shrink-0 mt-3 pb-2">
              <div className="flex items-center justify-end w-full gap-3">
                <Button 
                  onClick={handleSubmit} 
                  disabled={selectedIds.length === 0 || isSaving}
                  className="h-[38px] w-[110px] rounded-[8px] bg-[#2D6BFF] text-white font-medium text-[14px] hover:bg-[#2563EB] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {translateInterfaceText("Adicionar")}</Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="upload" className="flex flex-col h-full mt-0 pt-3">
            {/* Dropzone Premium - área toda clicável, sem botão */}
            {uploadFiles.length === 0 && (
              <label
                className={cn(
                  "border-2 border-dashed rounded-xl w-full h-[350px] flex flex-col items-center justify-center transition-all duration-200 cursor-pointer",
                  isDragging 
                    ? "border-[#2D6BFF] bg-[rgba(45,107,255,0.05)]" 
                    : "border-[#C7D3FF] bg-[#F8FAFF] hover:border-[#2D6BFF] hover:bg-[rgba(45,107,255,0.02)]"
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload className="h-12 w-12 text-[#2D6BFF] mb-4" />
                <p className="text-[18px] font-semibold text-[#1A1A1A] mb-2">
                  {translateInterfaceText("Arraste vídeos aqui")}</p>
                <p className="text-[14px] text-[#6B7280]">
                  {translateInterfaceText("ou clique para selecionar")}</p>
                
                <input
                  type="file"
                  multiple
                  accept="video/*"
                  className="hidden"
                  onChange={(e) => handleFileSelection(e.target.files)}
                />
              </label>
            )}

            {/* File list - Lista de progresso */}
            {uploadFiles.length > 0 && (
              <div className="max-h-[350px] overflow-y-auto space-y-3 [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-track]:bg-[#E8ECF2] [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#2D6BFF] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-[#2563EB]">
                {uploadFiles.map((item, index) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-4 p-3 rounded-lg bg-[#F9FAFB]"
                  >
                    {/* Thumbnail fixa 96x54 */}
                    <div className="w-[96px] h-[54px] rounded-[6px] border border-[#E2E8F0] bg-[#F1F5F9] flex-shrink-0 overflow-hidden">
                      {item.status !== 'generating-thumb' && (
                        <img 
                          src={item.thumbnail} 
                          alt={item.file.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {item.status === 'generating-thumb' && (
                        <div className="w-full h-full flex items-center justify-center">
                          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info e progresso */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-[14px] truncate text-[#1A1A1A] mb-2">{item.file.name}</h4>
                      
                      {item.status === 'uploading' && (
                        <>
                          <div className="w-full bg-[#E8ECF2] rounded-full h-1.5 overflow-hidden mb-1">
                            <div 
                              className="h-full bg-[#2D6BFF] transition-all duration-300"
                              style={{ width: `${item.progress || 0}%` }}
                            />
                          </div>
                          <p className="text-[12px] text-[#6B7280]">
                            {item.progress}{translateInterfaceText("% concluído")}</p>
                        </>
                      )}
                      
                      {item.status === 'completed' && (
                        <div className="flex items-center gap-3">
                          <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#E8F1FF] rounded-lg">
                            <div className="w-1.5 h-1.5 bg-[#2D6BFF] rounded-full" />
                            <span className="text-[12px] font-medium text-[#2D6BFF]">
                              {translateInterfaceText("Concluído")}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFileToRemove(index)}
                            className="text-[#DC2626] hover:text-[#DC2626] hover:bg-[#FEF2F2] h-8 text-[13px] font-medium"
                          >
                            {translateInterfaceText("Remover")}</Button>
                        </div>
                      )}
                      
                      {item.status !== 'uploading' && item.status !== 'completed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setFileToRemove(index)}
                          className="text-[#DC2626] hover:text-[#DC2626] hover:bg-[#FEF2F2] h-8 text-[13px] font-medium"
                        >
                          {translateInterfaceText("Remover")}</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex-shrink-0 mt-4">
              <div className="flex items-center justify-between w-full gap-3">
                <div>
                  {uploadFiles.length > 0 && !isUploading && uploadFiles.every(f => f.status === 'completed') && (
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.multiple = true;
                        input.accept = 'video/*';
                        input.onchange = (e) => {
                          const files = (e.target as HTMLInputElement).files;
                          handleFileSelection(files);
                        };
                        input.click();
                      }}
                      className="text-[14px] font-medium text-[#2D6BFF] hover:underline transition-all"
                    >
                      {translateInterfaceText("+ Adicionar vídeos")}</button>
                  )}
              </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
    
    {fileToRemove !== null && (
      <AlertDialog open={fileToRemove !== null} onOpenChange={() => setFileToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translateInterfaceText("Remover arquivo?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {translateInterfaceText("Tem certeza que deseja remover '")}{uploadFiles[fileToRemove]?.file.name}{translateInterfaceText("' da lista?")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setFileToRemove(null)}>
              {translateInterfaceText("Cancelar")}</Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                setUploadFiles(prev => prev.filter((_, i) => i !== fileToRemove));
                setFileToRemove(null);
              }}
            >
              {translateInterfaceText("Remover")}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )}

    </>
  );
}
